import { useCallback, useEffect, useMemo, useState } from 'react';

import type { FieldEvidence } from '@/components/fields/types';
import { getLocalStorageItem, setLocalStorageItem } from '@/utils/localStorage';

interface UseCaseInputStorageOptions {
  caseId?: string;
  callId?: string | null;
  initialInputData?: Record<string, any> | null;
  /** AI-extracted suggestion values keyed by field ID */
  extractedInputData?: Record<string, any> | null;
  /** AI-extracted hint text keyed by field ID */
  extractedInputHints?: Record<string, string> | null;
}

interface SuggestionState {
  [fieldId: string]: 'accepted' | 'rejected' | 'collapsed';
}

/** Props returned by getFieldSuggestionProps for a single field */
interface FieldSuggestionProps {
  suggestion: any | undefined;
  hint: string | undefined;
  isCollapsed: boolean;
  onAccept: () => void;
  onReject: () => void;
  onExpand: () => void;
  /**
   * Multi-value offer options ("PMS returned N values — choose one").
   * Supplied by the PMS prefill adapter only; the extraction flow always
   * offers a single suggestion. Clicking an option commits its value.
   */
  options?: { value: string; label?: string }[];
  onAcceptOption?: (value: string) => void;
  /**
   * Provenance/gap badge beside the field label (PMS / EDITED / ASK /
   * NOT IN PMS — R4: never a blank without a reason). PMS adapter only.
   */
  badge?: { kind: 'pms' | 'edited' | 'ask' | 'not_in_pms'; label: string };
  /** Present only when badge is EDITED: restores the original PMS value. */
  onRestore?: () => void;
  /**
   * Optional per-field extraction evidence for the suggested value. When
   * present (currently only the human-call output editing adapter supplies it),
   * the suggestion chip renders the Review button + reasoning lightbulb, matching
   * the read-only extracted-value display. The default case-input flow omits it.
   */
  evidence?: FieldEvidence[];
  sourceCallId?: string;
  onNavigateToCall?: (callId: string, evidenceTimeStart?: string, evidenceTimeEnd?: string) => void;
  onNavigateToAudit?: (auditId: string) => void;
}

export type GetFieldSuggestionProps = (fieldId: string, fieldType: string) => FieldSuggestionProps;

/**
 * Hook for managing case input form data in localStorage with auto-save/restore
 *
 * Features:
 * - Auto-saves form data on every change (when callId is available)
 * - Auto-restores from localStorage when callId changes
 * - Falls back to initialInputData when callId is not available (e.g., no call attached)
 * - Tracks suggestion state per field: accepted, rejected, or collapsed (textarea)
 * - Provides getFieldSuggestionProps(fieldId, fieldType) that consolidates all
 *   per-field suggestion logic (visibility, collapse, accept/reject/expand branching)
 * - 24-hour expiration (handled by existing localStorage utilities)
 * - Clears data on successful form submission
 *
 * Storage keys:
 * - `caseInput_{caseId}_{callId}` - Form data
 * - `caseSuggestions_{caseId}_{callId}` - Suggestion state (accepted/rejected/collapsed)
 */
export function useCaseInputStorage({
  caseId,
  callId,
  initialInputData,
  extractedInputData,
  extractedInputHints,
}: UseCaseInputStorageOptions) {
  // Form data state (restored from localStorage or initialized)
  const [formData, setFormData] = useState<Record<string, any>>({});

  // Unified suggestion state tracking (accepted, rejected, or collapsed per field)
  const [suggestionState, setSuggestionState] = useState<SuggestionState>({});

  // Track if data was restored from localStorage
  const [wasRestored, setWasRestored] = useState(false);

  // Derived localStorage keys
  const formDataKey = useMemo(
    () => (caseId && callId ? `caseInput_${caseId}_${callId}` : ''),
    [caseId, callId],
  );

  const suggestionStateKey = useMemo(
    () => (caseId && callId ? `caseSuggestions_${caseId}_${callId}` : ''),
    [caseId, callId],
  );

  // Load from localStorage when callId changes
  useEffect(() => {
    if (!caseId) {
      setFormData({});
      setSuggestionState({});
      setWasRestored(false);
      return;
    }

    // If no callId, we can't use localStorage, but we should still load initialInputData
    if (!callId) {
      setFormData(initialInputData || {});
      setSuggestionState({});
      setWasRestored(false);
      return;
    }

    const storedFormData = getLocalStorageItem<Record<string, any>>(formDataKey);
    const storedSuggestionState = getLocalStorageItem<SuggestionState>(suggestionStateKey);

    if (storedFormData || storedSuggestionState) {
      // Merge stored data with initial data (stored data takes precedence)
      const mergedFormData = { ...(initialInputData || {}), ...(storedFormData || {}) };
      setFormData(mergedFormData);
      setSuggestionState(storedSuggestionState || {});
      setWasRestored(true);
    } else {
      setFormData(initialInputData || {});
      setSuggestionState({});
      setWasRestored(false);
    }
  }, [caseId, callId, initialInputData, formDataKey, suggestionStateKey]);

  // Update form data (auto-saves to localStorage)
  const updateFormData = useCallback(
    (fieldId: string, value: any) => {
      setFormData(prev => {
        const newData = { ...prev, [fieldId]: value };
        if (formDataKey) setLocalStorageItem(formDataKey, newData);
        return newData;
      });
    },
    [formDataKey],
  );

  // Bulk update form data (used for initialization or reset)
  const setFormDataBulk = useCallback(
    (data: Record<string, any>) => {
      setFormData(data);
      if (formDataKey) setLocalStorageItem(formDataKey, data);
    },
    [formDataKey],
  );

  // Consolidated per-field suggestion props
  // Returns suggestion data + callbacks with textarea/non-textarea branching already applied
  const getFieldSuggestionProps: GetFieldSuggestionProps = useCallback(
    (fieldId: string, fieldType: string) => {
      const rawSuggestion = extractedInputData?.[fieldId];
      const hint = extractedInputHints?.[fieldId];
      const state = suggestionState[fieldId];
      const isVisible = state !== 'accepted' && state !== 'rejected';
      const suggestion = isVisible ? rawSuggestion : undefined;
      const isCollapsed = state === 'collapsed';
      const isTextarea = fieldType === 'textarea';

      return {
        suggestion,
        hint,
        isCollapsed,
        onAccept: () => {
          setSuggestionState(prev => {
            const newState = { ...prev, [fieldId]: 'accepted' as const };
            if (suggestionStateKey) setLocalStorageItem(suggestionStateKey, newState);
            return newState;
          });
        },
        onReject: () => {
          setSuggestionState(prev => {
            const value = isTextarea ? ('collapsed' as const) : ('rejected' as const);
            const newState = { ...prev, [fieldId]: value };
            if (suggestionStateKey) setLocalStorageItem(suggestionStateKey, newState);
            return newState;
          });
        },
        onExpand: () => {
          setSuggestionState(prev => {
            const newState = { ...prev };
            delete newState[fieldId];
            if (suggestionStateKey) setLocalStorageItem(suggestionStateKey, newState);
            return newState;
          });
        },
      };
    },
    [extractedInputData, extractedInputHints, suggestionState, suggestionStateKey],
  );

  // No-op function for backward compatibility
  // localStorage is never manually cleared - it relies on 24-hour automatic expiry
  // User decisions (accepted/rejected suggestions) and form edits persist until expiration
  const clearStorage = useCallback(() => {
    // Intentionally empty - localStorage expires automatically after 24 hours
  }, []);

  return {
    // State
    formData,
    wasRestored,

    // Form data handlers
    updateFormData,
    setFormDataBulk,

    // Suggestion props (replaces 8 individual suggestion props)
    getFieldSuggestionProps,

    // Storage management
    clearStorage,
  };
}
